// client/src/components/configuracoes/entrega/SecaoRetirada.js
import React, { useState, useCallback } from 'react';
import useAlert from '../../../hooks/useAlert';
import { lookupCep } from '../../../utils/cepService';
import { Package, Loader2, CheckCircle2 } from 'lucide-react';

const DIAS = [
    { value: 0, label: 'Domingo' },
    { value: 1, label: 'Segunda-feira' },
    { value: 2, label: 'Terça-feira' },
    { value: 3, label: 'Quarta-feira' },
    { value: 4, label: 'Quinta-feira' },
    { value: 5, label: 'Sexta-feira' },
    { value: 6, label: 'Sábado' },
];

// ============================================================
// SEÇÃO RETIRADA
// ============================================================
export const SecaoRetirada = ({ retirada, setRetirada, atualizarDia }) => {
    const { setAlert } = useAlert();
    const [isCepLoading, setIsCepLoading] = useState(false);
    const [cepEncontrado, setCepEncontrado] = useState(false);

    const setEndereco = useCallback((campo, valor) => {
        setRetirada(p => ({ ...p, enderecoCustom: { ...p.enderecoCustom, [campo]: valor } }));
    }, [setRetirada]);

    const handleCepChange = useCallback((e) => {
        setCepEncontrado(false);
        setEndereco('cep', e.target.value);
    }, [setEndereco]);

    const handleCepBlur = useCallback(async (e) => {
        const raw = e.target.value.replace(/\D/g, '');
        if (raw.length !== 8) return;
        setIsCepLoading(true);
        try {
            const addr = await lookupCep(raw);
            setRetirada(p => ({
                ...p,
                enderecoCustom: {
                    ...p.enderecoCustom,
                    cep:        addr.cep        || p.enderecoCustom.cep,
                    logradouro: addr.logradouro || p.enderecoCustom.logradouro,
                    bairro:     addr.bairro     || p.enderecoCustom.bairro,
                    cidade:     addr.localidade || p.enderecoCustom.cidade,
                    estado:     addr.uf         || p.enderecoCustom.estado,
                },
            }));
            setCepEncontrado(true);
            setAlert('CEP encontrado com sucesso!', 'success');
        } catch (err) {
            setAlert(err.message || 'Não foi possível consultar o CEP.', 'danger');
        } finally {
            setIsCepLoading(false);
        }
    }, [setRetirada, setAlert]);

    return (
    <>
        <div className="form-section">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h4 className="form-section-title" style={{ marginBottom: 0 }}>
                    <Package size={15} style={{ marginRight: 5, verticalAlign: 'middle' }} />
                    Habilitar Retirada no Local
                </h4>
                <label className="toggle-switch">
                    <input
                        type="checkbox"
                        checked={retirada.ativo}
                        onChange={e => setRetirada({ ativo: e.target.checked })}
                    />
                    <span className="toggle-slider" />
                </label>
            </div>
        </div>

        {retirada.ativo && (
            <>
                {/* Endereço */}
                <div className="form-section">
                    <h4 className="form-section-title">Endereço para Retirada</h4>
                    <div className="form-group" style={{ marginBottom: '1rem' }}>
                        <div style={{ display: 'flex', gap: '1.5rem' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.9rem' }}>
                                <input type="radio" checked={retirada.usarEnderecoUnidade} onChange={() => setRetirada({ usarEnderecoUnidade: true })} />
                                Usar endereço da unidade
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.9rem' }}>
                                <input type="radio" checked={!retirada.usarEnderecoUnidade} onChange={() => setRetirada({ usarEnderecoUnidade: false })} />
                                Endereço personalizado
                            </label>
                        </div>
                    </div>

                    {!retirada.usarEnderecoUnidade && (
                        <div className="form-grid-container-cols-2">
                            {/* CEP — primeiro, dispara o auto-fill */}
                            <div className="form-group">
                                <label className="form-label">CEP</label>
                                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                    <input
                                        className="form-input"
                                        type="text"
                                        maxLength={9}
                                        placeholder="00000-000"
                                        value={retirada.enderecoCustom.cep}
                                        onChange={handleCepChange}
                                        onBlur={handleCepBlur}
                                        disabled={isCepLoading}
                                        style={{ paddingRight: isCepLoading ? '2.25rem' : undefined }}
                                    />
                                    {isCepLoading && (
                                        <Loader2 size={16} className="animate-spin"
                                            style={{ position: 'absolute', right: '0.75rem', color: '#64748b', pointerEvents: 'none' }} />
                                    )}
                                </div>
                                {cepEncontrado ? (
                                    <div className="form-hint" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <CheckCircle2 size={13} style={{ color: 'var(--color-success, #16a34a)', flexShrink: 0 }} />
                                        Endereço preenchido automaticamente.
                                        <button type="button" onClick={() => setCepEncontrado(false)}
                                            style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-primary)', cursor: 'pointer', fontSize: 'inherit', textDecoration: 'underline' }}>
                                            Editar manualmente
                                        </button>
                                    </div>
                                ) : (
                                    <div className="form-hint">Digite o CEP e aguarde o preenchimento automático</div>
                                )}
                            </div>

                            <div className="form-group">
                                <label className="form-label">Número</label>
                                <input className="form-input" type="text" value={retirada.enderecoCustom.numero}
                                    onChange={e => setEndereco('numero', e.target.value)} />
                            </div>

                            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                <label className="form-label">Logradouro</label>
                                <input className="form-input" type="text" value={retirada.enderecoCustom.logradouro}
                                    readOnly={cepEncontrado}
                                    style={cepEncontrado ? { backgroundColor: 'var(--color-bg-subtle, #f1f5f9)', cursor: 'not-allowed' } : undefined}
                                    onChange={e => setEndereco('logradouro', e.target.value)} />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Bairro</label>
                                <input className="form-input" type="text" value={retirada.enderecoCustom.bairro}
                                    readOnly={cepEncontrado}
                                    style={cepEncontrado ? { backgroundColor: 'var(--color-bg-subtle, #f1f5f9)', cursor: 'not-allowed' } : undefined}
                                    onChange={e => setEndereco('bairro', e.target.value)} />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Complemento</label>
                                <input className="form-input" type="text" value={retirada.enderecoCustom.complemento}
                                    onChange={e => setEndereco('complemento', e.target.value)} />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Cidade</label>
                                <input className="form-input" type="text" value={retirada.enderecoCustom.cidade}
                                    readOnly={cepEncontrado}
                                    style={cepEncontrado ? { backgroundColor: 'var(--color-bg-subtle, #f1f5f9)', cursor: 'not-allowed' } : undefined}
                                    onChange={e => setEndereco('cidade', e.target.value)} />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Estado (UF)</label>
                                <input className="form-input" type="text" maxLength={2} value={retirada.enderecoCustom.estado}
                                    readOnly={cepEncontrado}
                                    style={cepEncontrado ? { backgroundColor: 'var(--color-bg-subtle, #f1f5f9)', cursor: 'not-allowed' } : undefined}
                                    onChange={e => setEndereco('estado', e.target.value.toUpperCase())} />
                            </div>
                        </div>
                    )}
                </div>

                {/* Prazo */}
                <div className="form-section">
                    <h4 className="form-section-title">Prazo Mínimo</h4>
                    <div className="form-grid-container-cols-2">
                        <div className="form-group">
                            <label className="form-label">Antecedência mínima (horas)</label>
                            <input
                                className="form-input"
                                type="number"
                                min="0"
                                value={retirada.prazoMinimoHoras}
                                onChange={e => setRetirada({ prazoMinimoHoras: e.target.value })}
                            />
                            <small style={{ color: '#64748b', fontSize: '0.8rem' }}>0 = sem restrição</small>
                        </div>
                    </div>
                </div>

                {/* Dias disponíveis */}
                <div className="form-section">
                    <h4 className="form-section-title">Dias e Horários de Retirada</h4>
                    <div className="table-container" style={{ marginTop: 0 }}>
                        <div className="table-wrapper">
                            <table className="styled-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 50 }}>Ativo</th>
                                        <th>Dia da Semana</th>
                                        <th>Abertura</th>
                                        <th>Fechamento</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {retirada.diasDisponiveis.map((dia, idx) => (
                                        <tr key={dia.diaSemana} style={{ opacity: dia.ativo ? 1 : 0.45 }}>
                                            <td style={{ textAlign: 'center' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={dia.ativo}
                                                    onChange={e => atualizarDia(idx, 'ativo', e.target.checked)}
                                                />
                                            </td>
                                            <td style={{ fontWeight: 500 }}>{DIAS[idx].label}</td>
                                            <td>
                                                <input
                                                    className="form-input"
                                                    type="time"
                                                    value={dia.horarioInicio}
                                                    disabled={!dia.ativo}
                                                    onChange={e => atualizarDia(idx, 'horarioInicio', e.target.value)}
                                                    style={{ width: 110 }}
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    className="form-input"
                                                    type="time"
                                                    value={dia.horarioFim}
                                                    disabled={!dia.ativo}
                                                    onChange={e => atualizarDia(idx, 'horarioFim', e.target.value)}
                                                    style={{ width: 110 }}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Instruções */}
                <div className="form-section">
                    <h4 className="form-section-title">Instruções para o Cliente</h4>
                    <div className="form-group">
                        <textarea
                            className="form-input"
                            rows={3}
                            value={retirada.instrucoes}
                            onChange={e => setRetirada({ instrucoes: e.target.value })}
                            placeholder="Ex: Apresente o CPF na retirada. Retiradas somente das 9h às 18h."
                        />
                    </div>
                </div>
            </>
        )}
    </>
    );
};

export default SecaoRetirada;
